import { useId } from "react";
import { cn } from "@/lib/utils";
import type { Language } from "@/lib/i18n/types";

/**
 * A language's flag, drawn inline.
 *
 * Not a flag emoji: Windows ships no flag glyphs, so "🇪🇪" renders there as the
 * letters "EE" — and the people choosing a protocol's language are mostly on
 * Windows. Both flags are cropped into the same 3:2 box so a pair of them
 * lines up; the ring keeps Estonia's white band visible on a white surface.
 *
 * Decorative: the control carrying it supplies the accessible name.
 */
export function LanguageFlag({ language, className }: { language: Language; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block h-4 w-6 shrink-0 overflow-hidden rounded-[2px] ring-1 ring-black/15",
        className
      )}
    >
      {language === "et" ? <EstonianFlag /> : <BritishFlag />}
    </span>
  );
}

/**
 * Fills the flag's box. Important, because hosts size every svg inside them —
 * Button's `[&_svg]:size-4` squeezed the flag to 16px of its 24px box and left
 * the rest of it blank.
 */
const FLAG_SVG = "block !h-full !w-full";

function EstonianFlag() {
  return (
    <svg viewBox="0 0 33 21" preserveAspectRatio="xMidYMid slice" className={FLAG_SVG}>
      <rect width="33" height="7" fill="#0072CE" />
      <rect y="7" width="33" height="7" fill="#000000" />
      <rect y="14" width="33" height="7" fill="#FFFFFF" />
    </svg>
  );
}

function BritishFlag() {
  // The red saltire is counterchanged — offset from the white one — which is
  // what the clip does: each red diagonal only paints its own half of each arm.
  // The id is per instance because two flags on one page would otherwise share
  // (and fight over) one clipPath. React's ids carry colons, which are
  // stripped so the `url(#…)` reference stays a plain fragment.
  const clipId = `gb-flag-${useId().replace(/:/g, "")}`;
  return (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" className={FLAG_SVG}>
      <clipPath id={clipId}>
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" strokeWidth="6" />
      <path
        d="M0,0 L60,30 M60,0 L0,30"
        clipPath={`url(#${clipId})`}
        stroke="#C8102E"
        strokeWidth="4"
      />
      <path d="M30,0 v30 M0,15 h60" stroke="#FFFFFF" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
}
